<template>
  <section id="mainSection" :style="mainStyle">
    <vue-headful
      :title="
        $t('TITLE_TYPE_NAME', { type: $tc(itemType, 1), name: (item && item.name) || '' })
          | formatTitle
      "
    ></vue-headful>
    <slot name="top-menu"></slot>

    <div class="ui top attached inverted purple menu" v-if="isEditMode && isDiff">
      <div class="item" style="font-weight: bold;">
        {{ $t('CHANGE', { fromNow, tag: changelog.tag || $t('UNTAGGED') }) }}
      </div>
      <div class="right menu">
        <a class="item" @click="abortChange">
          {{ $t('CHANGE_ABORT') }}
        </a>
        <a class="item" @click="revertChange">
          {{ $t('CHANGE_REVERT') }}
        </a>
      </div>
    </div>
    <div
      class="ui center basic segment"
      :class="{
        loading: isLoading && !isEditMode,
        contentScrolls: contentHandlesScroll,
        top: !isDiff,
        attached: isDiff || hasTabsInFooter,
        diff: isDiff
      }"
    >
      <form :class="[formClass]" autocomplete="off">
        <template v-if="!noHeader">
          <h3 class="ui horizontal left aligned divider header capitalize-fl" v-if="header">
            {{ header }}
          </h3>
          <h3 class="ui horizontal left aligned divider header" v-else-if="isEditMode">
            {{ $t('EDIT', { thing: $tc(itemType, 1), name: item && item.name }) }}
          </h3>
          <h3 class="ui horizontal left aligned divider header" v-else>
            {{ $t('CREATE', { thing: $tc(itemType, 1), name: item && item.name }) }}
          </h3>
          <div class="ui top attached red label" v-if="isOutdated">
            {{ $t('WARN_OUTDATED', { thing: $tc(itemType, 1) }) }}
          </div>
        </template>

        <div class="ui basic segment" id="contentArea">
          <slot name="fieldset"></slot>
        </div>
      </form>
    </div>

    <div
      class="ui tabular bottom attached menu"
      :class="{ paddingTop: !hasTabsInFooter }"
      v-if="isEditMode"
    >
      <slot name="left-bottom-menu">
        <loading-confirm-button
          class="ui right icon red button"
          :onClick="onDelete"
          :disabled="!canDelete || isOutdated || isDiff"
          :confirmMessage="
            $t('DELETE_CONFIRM_MESSAGE', {
              thingType: $tc(itemType, 1),
              thingName: item && item.name
            })
          "
        >
          <i class="trash icon"></i>
        </loading-confirm-button>
      </slot>

      <div class="right secondary menu" :class="{ paddingTop: hasTabsInFooter }">
        <slot name="rightBottomMenuSlot1"></slot>

        <changelog-button
          ref="changelogButton"
          :disabled="isOutdated"
          :typename="typename"
          :item="item"
          :itemType="itemType"
          @change-chosen="onChangeChosen"
          @revert="onRevert"
          :saveFn="onUpdateAction"
          v-if="item"
        />

        <slot name="rightBottomMenuSlot2"></slot>

        <loading-button
          class="ui left labeled icon button"
          :onClick="onUpdateAction"
          :disabled="!canUpdate || isOutdated || isDiff || !isItemEdited || !isValidated"
        >
          {{ $t('UPDATE', { thing: '' }) }}
          <i class="checkmark icon"></i>
        </loading-button>
      </div>
    </div>

    <div
      class="ui tabular bottom menu"
      :class="{ paddingTop: !hasTabsInFooter, tabular: hasTabsInFooter }"
      v-else
    >
      <div class="right secondary menu">
        <slot name="rightBottomCreateMenuSlot"></slot>
        <loading-button
          class="ui right labeled icon button"
          :onClick="onCreate"
          :disabled="isOutdated || !isValidated"
        >
          {{ $t('CREATE', { thing: '' }) }}
          <i class="checkmark icon"></i>
        </loading-button>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@charset "utf-8";

.paddingTop {
  padding-top: 8px;
}

#mainSection {
  height: 100%;
  display: flex;
  flex-direction: column;

  .center.segment {
    flex: 1 0 100px;
    overflow-y: auto;
    margin: 0;
    &.contentScrolls form {
      height: 100%;
      overflow-y: initial;
      display: flex;
      flex-direction: column;

      .divider {
        height: 1px;
      }

      #contentArea {
        flex: 1 0 100px;
      }
    }
  }

  .bottom.menu {
    margin: 0;
  }
}
</style>
<script>
// @flow
import { LoadingButton, LoadingConfirmButton } from 'ui'
import ChangelogButton from '../../Changelog/ChangelogButton'
import _ from 'lodash'
import moment from 'moment'
import { isDeepEmpty } from 'utility'

import { MutationTypes } from 'store'

export default {
  name: 'main-section',
  components: { LoadingButton, LoadingConfirmButton, ChangelogButton },

  props: {
    formClass: {
      type: String,
      default: 'ui info form'
    },
    header: {
      type: String,
      default: () => ''
    },
    noHeader: {
      type: Boolean,
      default: () => false
    },
    isValidated: {
      type: Boolean,
      default: () => true
    },
    isEditMode: {
      type: Boolean,
      default: () => false
    },
    isLoading: {
      type: Boolean,
      default: () => false
    },
    isDiff: {
      type: Boolean,
      default: () => false
    },
    isOutdated: {
      type: Boolean,
      default: () => false
    },
    onUpdate: {
      type: Function,
      default: () => ''
    },
    canUpdate: {
      type: Boolean,
      default: () => true
    },
    onCreate: {
      type: Function,
      default: () => ''
    },
    canDelete: {
      type: Boolean,
      default: () => true
    },
    onDelete: {
      type: Function,
      default: () => ''
    },
    itemType: {
      type: String,
      default: () => ''
    },
    typename: {
      type: String,
      default: () => ''
    },
    item: {
      type: Object,
      default: () => {}
    },
    hasTabsInFooter: {
      type: Boolean,
      default: () => false
    },
    mainStyle: {
      type: Object,
      default: () => {}
    },
    isEdited: {
      type: Boolean,
      default: () => false
    },
    contentHandlesScroll: {
      type: Boolean,
      default: () => false
    }
  },

  data() {
    return {
      savedItem: {},
      saveInProgress: false,
      changelog: {},
      abortChange: () => {},
      revertChange: () => {}
    }
  },

  computed: {
    fromNow() {
      if (this.changelog.ts) {
        return moment(this.changelog.ts * 1000)
          .locale(this.$locale.current() || 'en')
          .fromNow()
      }
      return ''
    },
    isItemEdited() {
      console.debug('edited', this.savedItem, this.item)
      return (
        !_.get(this.item, 'isDiff', false) &&
        (!_.isEqual(this.savedItem, this.item) || this.isEdited)
      )
    },
    savesRequested() {
      return this.$store.state.savesRequested
    }
  },

  methods: {
    onUpdateAction() {
      this.saveInProgress = true
      return this.onUpdate()
        .then(() => {
          if (this.item) {
            this.savedItem = _.cloneDeep(this.item)
            this.$refs.changelogButton.refreshChangesData()
          }
        })
        .finally(() => {
          this.saveInProgress = false
        })
    },
    updateOrCreateIfNeeded() {
      if (this.saveInProgress) return
      if (this.isEditMode) {
        if (this.isItemEdited) this.onUpdateAction()
      } else {
        this.onCreateAction()
      }
    },
    onCreateAction() {
      this.saveInProgress = true
      return this.onCreate().finally(() => {
        this.saveInProgress = false
      })
    },
    onChangeChosen(change) {
      this.changelog = change.log
      // setup abort, revert callbacks
      if (change.actions) {
        this.abortChange = change.actions.abort
        this.revertChange = change.actions.revert
      }
      this.$emit('change-chosen', change)
    },
    onRevert(target) {
      this.changelog = {}
      this.$emit('revert', target)
    }
  },

  watch: {
    item: {
      handler: function(item, prevItem) {
        if (isDeepEmpty(this.savedItem) || item.id !== prevItem.id) {
          this.savedItem = _.cloneDeep(item)
          this.$store.commit(MutationTypes.SET_CLEAN)
        }
      },
      deep: true
    },
    isItemEdited(newVal, oldVal) {
      if (newVal === oldVal) return
      this.$store.commit(newVal ? MutationTypes.SET_DIRTY : MutationTypes.SET_CLEAN)
    },
    savesRequested() {
      this.updateOrCreateIfNeeded()
    }
  }
}
//
</script>
